import {
    UI_CONTEXT_REVIEW_LESSON,
    UI_CONTEXT_REVIEW_LESSON_AUDIO,
    UI_CONTEXT_REVIEW_PREVIOUS_MATERIAL,
    UI_CONTEXT_REVIEW_PREVIOUS_MATERIAL_AUDIO,
} from 'TutorBotConversation';
import {
    type LessonPlayerBotAudioUiContext,
    type ContentInfoForReviewLessonUiContext,
    type ReviewPreviousMaterialVoiceAgentAttributesWithThisLesson,
    type ContentInfoForReviewPreviousMaterialUiContext,
    type ReviewPreviousMaterialVoiceAgentAttributesWithPreviousLesson,
    type ReviewPreviousMaterialClientContextWithPreviousLesson,
    type ReviewPreviousMaterialClientContextWithThisLesson,
    type AiTutorClientContext,
    type LessonPlayerClientContext,
    type LessonPlayerBotChatUiContext,
    type LessonPlayerBotContentInfo,
} from '../TutorBotAiTutorChat.types';
import { isLessonPlayerAlgorithmUiContext } from './LessonPlayerBotUiContext';
import {
    isContentInfoForLessonPlayerAlgorithm,
    isContentInfoForReviewLessonUiContent,
    isContentInfoForReviewPreviousMaterialUiContext,
} from './typeGuards';

/*
    The functions in this file create attributes to be passed to the tutorbot
    service. getTutorBotChatClientContext provides attributes for text chat algorithms.
    getVoiceAgentAttrs provides attributes for voice agents.

    Both functions take the content info generated in the player view model as inputs.
*/

export function getTutorBotChatClientContext(
    uiContext: LessonPlayerBotChatUiContext,
    contentInfo: LessonPlayerBotContentInfo,
): AiTutorClientContext {
    if (isLessonPlayerAlgorithmUiContext(uiContext)) {
        if (!isContentInfoForLessonPlayerAlgorithm(contentInfo)) {
            throw new Error(
                'uiContext indicates we are using the lesson player algorithm, but contentInfo is not a ContentInfoForLessonPlayerAlgorithm',
            );
        }
        const clientContext: LessonPlayerClientContext = {
            uiContext: 'lesson_player',
            lessonId: contentInfo.lessonId,
            lessonTitle: contentInfo.lessonTitle,
            lessonVersionId: contentInfo.lessonVersionId,
        };
        return clientContext;
    }

    if (uiContext === UI_CONTEXT_REVIEW_PREVIOUS_MATERIAL) {
        if (!isContentInfoForReviewPreviousMaterialUiContext(contentInfo)) {
            throw new Error(`invalid contentInfo for ${UI_CONTEXT_REVIEW_PREVIOUS_MATERIAL} ui context`);
        }
        const clientContext: ReviewPreviousMaterialClientContextWithPreviousLesson = {
            uiContext: 'review_previous_material',
            targetLesson: 'previous_lesson',
            targetLessonId: contentInfo.targetLessonId,
            streamId: contentInfo.streamId,
        };
        return clientContext;
    }

    if (uiContext === UI_CONTEXT_REVIEW_LESSON) {
        if (!isContentInfoForReviewLessonUiContent(contentInfo)) {
            throw new Error(`invalid contentInfo for ${UI_CONTEXT_REVIEW_LESSON} ui context`);
        }
        const clientContext: ReviewPreviousMaterialClientContextWithThisLesson = {
            uiContext: 'review_lesson',
            targetLesson: 'this_lesson',
            lessonId: contentInfo.lessonId,
            streamId: contentInfo.streamId,
        };
        return clientContext;
    }

    throw new Error(`Unsupported uiContext: ${uiContext}`);
}

export function getVoiceAgentAttrs({
    uiContext,
    contentInfo,
}: {
    uiContext: LessonPlayerBotAudioUiContext;
    contentInfo: LessonPlayerBotContentInfo;
}) {
    if (uiContext === UI_CONTEXT_REVIEW_PREVIOUS_MATERIAL_AUDIO) {
        if (!isContentInfoForReviewPreviousMaterialUiContext(contentInfo)) {
            throw new Error(`invalid contentInfo for ${UI_CONTEXT_REVIEW_PREVIOUS_MATERIAL_AUDIO} ui context`);
        }

        return getVoiceAgentAttrsForReviewPreviousMaterialUiContext({ contentInfo });
    }

    if (uiContext === UI_CONTEXT_REVIEW_LESSON_AUDIO) {
        if (!isContentInfoForReviewLessonUiContent(contentInfo)) {
            throw new Error(`invalid contentInfo for ${UI_CONTEXT_REVIEW_LESSON_AUDIO} ui context`);
        }

        return getVoiceAgentAttrsForReviewLessonUiContext({ contentInfo });
    }

    throw new Error(`Unsupported uiContext: ${uiContext}`);
}

export function getVoiceAgentAttrsForReviewPreviousMaterialUiContext({
    contentInfo,
}: {
    contentInfo: ContentInfoForReviewPreviousMaterialUiContext;
}): ReviewPreviousMaterialVoiceAgentAttributesWithPreviousLesson {
    return {
        targetLesson: 'previous_lesson',
        streamId: contentInfo.streamId,
        currentLessonId: contentInfo.lessonId,
        targetLessonId: contentInfo.targetLessonId,
    };
}

export function getVoiceAgentAttrsForReviewLessonUiContext({
    contentInfo,
}: {
    contentInfo: ContentInfoForReviewLessonUiContext;
}): ReviewPreviousMaterialVoiceAgentAttributesWithThisLesson {
    return {
        targetLesson: 'this_lesson',
        streamId: contentInfo.streamId,
        lessonId: contentInfo.lessonId,
    };
}
